import React from 'react';
import {
  TableRow, TableCell, IconButton, Collapse, Box,
  Stack, Typography, Chip,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  SwapHoriz as SwapHorizIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { WorkflowHistoryItem } from '../../../../types/workflow/activity';
import {
  getCategoryLabel, getItemIcon, getDotColor, getCategoryChipColor,
  formatTime, getVisibleMetadata,
} from '../../workflowHistoryUtils';
import { WorkflowStatusChip } from '../../WorkflowStatusChip';

const cellSx = { py: 0.75, verticalAlign: 'middle' } as const;

interface WorkflowHistoryRowProps {
  item: WorkflowHistoryItem;
  expanded: boolean;
  onToggle: () => void;
  hasMetadata: boolean;
}

const renderTransitionContent = (item: WorkflowHistoryItem) => (
  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
    {item.fromStatus ? (
      <>
        <WorkflowStatusChip statusCode={item.fromStatus.code} statusName={item.fromStatus.name} size="small" />
        <SwapHorizIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      </>
    ) : (
      <Typography variant="body2" color="text.secondary">
        Created as
      </Typography>
    )}
    {item.toStatus && (
      <WorkflowStatusChip statusCode={item.toStatus.code} statusName={item.toStatus.name} size="small" />
    )}
  </Stack>
);

export const WorkflowHistoryRow: React.FC<WorkflowHistoryRowProps> = ({
  item,
  expanded,
  onToggle,
  hasMetadata,
}) => {
  const visibleMetadata = getVisibleMetadata(item.metadata);

  return (
    <>
      {/* Summary Row */}
      <TableRow
        hover
        sx={{
          cursor: hasMetadata ? 'pointer' : 'default',
          borderLeft: 3,
          borderColor: `${getDotColor(item)}.main`,
          '& > .MuiTableCell-root': { borderBottom: expanded ? 'none' : undefined },
        }}
        onClick={hasMetadata ? onToggle : undefined}
      >
        {/* Expand toggle */}
        <TableCell sx={{ ...cellSx, width: 40 }}>
          {hasMetadata ? (
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
              {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 30 }} />
          )}
        </TableCell>

        {/* Time + Icon */}
        <TableCell sx={{ ...cellSx, width: 90 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ color: `${getDotColor(item)}.main`, display: 'flex', alignItems: 'center' }}>
              {getItemIcon(item)}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
              {formatTime(item.changedAt)}
            </Typography>
          </Stack>
        </TableCell>

        {/* Category chip */}
        <TableCell sx={{ ...cellSx, width: 130 }}>
          <Chip
            label={getCategoryLabel(item.category)}
            size="small"
            color={getCategoryChipColor(item)}
            variant="outlined"
            sx={{ height: 22, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
          />
        </TableCell>

        {/* Event content */}
        <TableCell sx={cellSx}>
          {item.itemType === 'Transition'
            ? renderTransitionContent(item)
            : <Typography variant="body2">{item.description}</Typography>}
        </TableCell>

        {/* User */}
        <TableCell sx={{ ...cellSx, width: 120 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <PersonIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {item.changedByName}
            </Typography>
          </Stack>
        </TableCell>
      </TableRow>

      {/* Expanded Detail Row */}
      {hasMetadata && (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ py: 1.5, px: 2 }}>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ pl: 1, borderLeft: 2, borderColor: 'divider' }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  {visibleMetadata.map(([key, value]) => (
                    <Typography key={key} variant="caption" color="text.secondary">
                      <Box component="span" sx={{ fontWeight: 600 }}>{key}:</Box> {value}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
